import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import {
  getSoaPdfColumnWidths,
  getSoaPdfFontSizes,
  stripFootnoteNumberForSoaExport,
  SOA_PDF_USABLE_WIDTH_PT,
  soaDataToTableRows,
  isSoaSectionBannerRow,
  listSoaLegacyPayloadsForExport,
  splitSoaTableForPdfWidth,
  buildSoaHeaderGroups,
} from '../../utils/soaTableExportModel';
import { chunkSoaTableRows } from '../../utils/pdfTablePagination';
import { prepareSoaRowsForPdf } from '../../utils/pdfSoaRowSplit';
import { toSentenceCaseTable } from '../../utils/tableTextFormat';
import { PDF_FONT_BOLD, PDF_FONT_REGULAR, sanitizePdfPt } from '../../utils/pdfFontSetup';
import { renderHybridPdfText } from '../../utils/renderHybridPdfText';
import {
  PDF_TABLE_CELL_LINE_HEIGHT,
  PDF_TABLE_CELL_PAD_PT,
} from '../../utils/pdfTableLayoutRules';
import PdfTableCaption from './PdfTableCaption';

const BORDER = '#000000';
const CELL_PAD = PDF_TABLE_CELL_PAD_PT;

const styles = StyleSheet.create({
  table: {
    width: SOA_PDF_USABLE_WIDTH_PT,
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: BORDER,
    marginBottom: 6,
    flexGrow: 0,
    alignSelf: 'flex-start',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'stretch',
    borderBottomWidth: 1,
    borderColor: BORDER,
    flexGrow: 0,
  },
  cell: {
    flexShrink: 0,
    flexGrow: 0,
    padding: CELL_PAD,
    borderRightWidth: 1,
    borderRightColor: BORDER,
  },
  cellLast: {
    borderRightWidth: 0,
  },
  center: { textAlign: 'center' },
  left: { textAlign: 'left' },
  scheduleBlock: {
    marginBottom: 12,
  },
  footnotesTitle: {
    fontFamily: PDF_FONT_BOLD,
    marginTop: 6,
    marginBottom: 3,
  },
  footnoteLine: {
    fontFamily: PDF_FONT_REGULAR,
    marginBottom: 2,
    textAlign: 'left',
  },
  noteCell: {
    backgroundColor: '#fafafa',
  },
});

function safeColWidth(widthPt, fallback = 40) {
  const n = Number(widthPt);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function normalizeColWidths(rawWidths, targetTotal) {
  const total = Math.max(1, Number(targetTotal) || SOA_PDF_USABLE_WIDTH_PT);
  if (!rawWidths?.length) return [total];
  const widths = rawWidths.map((w) => safeColWidth(w, total / rawWidths.length));
  const sum = widths.reduce((a, b) => a + b, 0);
  if (sum > 0 && Math.abs(sum - total) > 0.01) {
    const scale = total / sum;
    for (let i = 0; i < widths.length; i += 1) {
      widths[i] = Math.max(2, Math.round(widths[i] * scale * 100) / 100);
    }
    const drift = total - widths.reduce((a, b) => a + b, 0);
    widths[widths.length - 1] = Math.max(2, widths[widths.length - 1] + drift);
  }
  return widths;
}

function renderCellLines(text, textStyle, align, keyPrefix) {
  const raw = String(text ?? '').trim();
  if (!raw) return null;

  const lines = raw.includes('\n')
    ? raw.split(/\n/).map((l) => l.trim()).filter(Boolean)
    : [raw];

  return lines.map((line, li) => (
    <Text
      key={`${keyPrefix}-${li}`}
      style={[
        textStyle,
        { lineHeight: PDF_TABLE_CELL_LINE_HEIGHT },
        align === 'left' ? styles.left : styles.center,
      ]}
      wrap
    >
      {renderHybridPdfText(line)}
    </Text>
  ));
}

function SoaHeaderRow({ header, colWidths, lastCol, fonts }) {
  return (
    <View wrap={false} style={styles.row}>
      {header.map((label, i) => {
        const isFirst = i === 0;
        const isLast = i === lastCol;
        const colWidth = safeColWidth(colWidths[i]);
        const display = isFirst || isLast ? toSentenceCaseTable(label) : label;
        const textStyle = {
          fontFamily: PDF_FONT_BOLD,
          fontSize: sanitizePdfPt(isFirst || isLast ? fonts.headerPt : fonts.visitHeaderPt, 9),
        };
        const align = isFirst || isLast ? 'left' : 'center';

        return (
          <View
            key={i}
            style={[styles.cell, { width: colWidth }, isLast ? styles.cellLast : null]}
          >
            {renderCellLines(display, textStyle, align, `hdr-${i}`)}
          </View>
        );
      })}
    </View>
  );
}

function SoaDataRow({ rowData, header, colWidths, lastCol, fonts, rowIdx }) {
  const isBanner = isSoaSectionBannerRow(rowData, header);
  const isContinuation = Boolean(rowData._soaContinuation);

  return (
    <View
      wrap={false}
      style={[styles.row, isBanner ? { backgroundColor: '#f0f0f0' } : null]}
    >
      {header.map((colKey, colIdx) => {
        const raw = String(rowData[colKey] ?? '');
        const isFirst = colIdx === 0;
        const isKeynotes = header[lastCol] === 'Key Notes' && colIdx === lastCol;
        const isVisit = !isFirst && !isKeynotes;
        const colWidth = safeColWidth(colWidths[colIdx]);

        let textStyle = {
          fontFamily: PDF_FONT_REGULAR,
          fontSize: sanitizePdfPt(isVisit ? fonts.visitBodyPt : fonts.bodyPt, 9),
        };
        let align = 'center';
        let display = raw;

        if (isBanner && isFirst && !isContinuation) {
          textStyle = {
            fontFamily: PDF_FONT_BOLD,
            fontSize: sanitizePdfPt(fonts.headerPt, 9),
          };
          display = toSentenceCaseTable(raw);
          align = 'left';
        } else if (isFirst) {
          align = 'left';
        } else if (isKeynotes) {
          align = 'left';
          textStyle.fontSize = sanitizePdfPt(fonts.footnotePt, 8);
        }

        return (
          <View
            key={colIdx}
            style={[styles.cell, { width: colWidth }, colIdx === lastCol ? styles.cellLast : null]}
          >
            {renderCellLines(
              display || (isVisit ? ' ' : ''),
              textStyle,
              align,
              `soa-${rowIdx}-${colIdx}`,
            )}
          </View>
        );
      })}
    </View>
  );
}

/**
 * Grouped 2-tier header: consecutive visit columns sharing the same period (e.g. Cycle 1's
 * Day 1 / Day 15) render one shared period cell spanning their combined width, with per-column
 * day text in a second row beneath — instead of repeating the full "period\nday" text in every
 * narrow column, which was the single biggest contributor to SOA headers running 100pt+ tall
 * and starving the rest of the page.
 *
 * Period (tier 1) and day (tier 2) ALWAYS render as two separate rows, for every column, not
 * just columns that share a period with a neighbor. Combining them into one cell for a
 * single-column group looked cheaper on paper, but real SOA data routinely has long text in
 * BOTH independently — combining made that cell as tall as two rows would've needed anyway, so
 * a multi-span group's tier-2 row became pure added height with no offsetting savings,
 * regressing the "grouped" header to taller than the original flat one. Splitting
 * unconditionally means tier 1 only ever holds short period text and tier 2 only ever holds
 * one day text (never both concatenated) — never taller than the flat layout, usually shorter.
 * Must stay in sync with estimateSoaHeaderHeightPt's grouped branch, which mirrors this shape.
 */
function SoaGroupedHeaderRows({ header, colWidths, fonts, visitMeta }) {
  const groups = buildSoaHeaderGroups(visitMeta);
  const labelWidth = safeColWidth(colWidths[0]);
  const groupTextStyle = { fontFamily: PDF_FONT_BOLD, fontSize: sanitizePdfPt(fonts.visitHeaderPt, 8) };
  const labelTextStyle = { fontFamily: PDF_FONT_BOLD, fontSize: sanitizePdfPt(fonts.headerPt, 9) };

  let colIdx = 1;
  const tier1Cells = groups.map((group, gi) => {
    const groupWidth = colWidths
      .slice(colIdx, colIdx + group.span)
      .reduce((sum, w) => sum + safeColWidth(w), 0);
    const display = group.period || group.days[0] || '—';
    colIdx += group.span;
    return (
      <View
        key={gi}
        style={[styles.cell, { width: groupWidth }, gi === groups.length - 1 ? styles.cellLast : null]}
      >
        {renderCellLines(display, groupTextStyle, 'center', `hg-${gi}`)}
      </View>
    );
  });

  colIdx = 1;
  const tier2Cells = groups.map((group, gi) => {
    // Period was empty and its day already stood in for it in tier 1 above — nothing left to
    // show here, but still emit blank cell(s) so column widths stay aligned with the body.
    if (!group.period) {
      const cells = group.days.map((_, i) => {
        const w = safeColWidth(colWidths[colIdx + i]);
        const isLast = gi === groups.length - 1 && i === group.days.length - 1;
        return <View key={i} style={[styles.cell, { width: w }, isLast ? styles.cellLast : null]} />;
      });
      colIdx += group.span;
      return cells;
    }
    const dayCells = group.days.map((day, i) => {
      const w = safeColWidth(colWidths[colIdx + i]);
      const isLast = gi === groups.length - 1 && i === group.days.length - 1;
      return (
        <View key={i} style={[styles.cell, { width: w }, isLast ? styles.cellLast : null]}>
          {renderCellLines(day, groupTextStyle, 'center', `hd-${gi}-${i}`)}
        </View>
      );
    });
    colIdx += group.span;
    return dayCells;
  });

  return (
    <React.Fragment>
      <View wrap={false} style={styles.row}>
        <View style={[styles.cell, { width: labelWidth }]}>
          {renderCellLines(toSentenceCaseTable(header[0]), labelTextStyle, 'left', 'hdr-label')}
        </View>
        {tier1Cells}
      </View>
      <View wrap={false} style={styles.row}>
        <View style={[styles.cell, { width: labelWidth }]} />
        {tier2Cells}
      </View>
    </React.Fragment>
  );
}

/**
 * Key Notes for an activity render as their own full-width row directly beneath it, instead
 * of a narrow trailing column — a single footnote sentence used to wrap into 7-8 lines in an
 * ~18%-width column, inflating that row's height enough to strand it alone on a page. At the
 * full table width the same text wraps into 1-3 lines, and it reads more naturally attached
 * to the specific activity it annotates rather than sitting in a column most rows leave blank.
 */
function SoaNoteRow({ text, totalWidth, fonts, rowIdx }) {
  const trimmed = String(text ?? '').trim();
  if (!trimmed) return null;

  const textStyle = {
    fontFamily: PDF_FONT_REGULAR,
    fontStyle: 'italic',
    fontSize: sanitizePdfPt(fonts.footnotePt, 8),
  };

  return (
    <View wrap={false} style={styles.row}>
      <View style={[styles.cell, styles.cellLast, styles.noteCell, { width: totalWidth }]}>
        {renderCellLines(trimmed, textStyle, 'left', `note-${rowIdx}`)}
      </View>
    </View>
  );
}

function SoaTablePart({ header, rows, fontConfig, caption, partKey, scheduleIdx, visitMeta, sectionTitle }) {
  if (!header.length || !rows.length) return null;

  const rawWidths = getSoaPdfColumnWidths(header.length, SOA_PDF_USABLE_WIDTH_PT, {
    hasKeyNotesColumn: header[header.length - 1] === 'Key Notes',
  }) || [];
  const colWidths = normalizeColWidths(rawWidths, SOA_PDF_USABLE_WIDTH_PT);
  const fonts = getSoaPdfFontSizes(header.length, fontConfig);
  const lastCol = header.length - 1;
  const totalTableWidth = colWidths.reduce((sum, w) => sum + w, 0);
  // Grouped header needs one {period, day} entry per visit column of THIS part specifically —
  // fall back to the flat, ungrouped header if that's not available for any reason.
  const useGroupedHeader = Array.isArray(visitMeta) && visitMeta.length === header.length - 1;

  const preparedRows = prepareSoaRowsForPdf(rows, header, colWidths, fonts);
  const rowChunks = chunkSoaTableRows(preparedRows, header, {
    colWidths,
    fonts,
    visitMeta: useGroupedHeader ? visitMeta : null,
    firstPageReserved: caption ? 48 : 24,
  });

  return (
    <View style={styles.scheduleBlock} wrap>
      {rowChunks.map((chunk, chunkIdx) => {
        const headerRows = useGroupedHeader ? (
          <SoaGroupedHeaderRows header={header} colWidths={colWidths} fonts={fonts} visitMeta={visitMeta} />
        ) : (
          <SoaHeaderRow header={header} colWidths={colWidths} lastCol={lastCol} fonts={fonts} />
        );

        if (chunkIdx === 0) {
          // The section title (rendered by the caller, see sectionTitle prop) + caption +
          // this first chunk are wrapped together as ONE atomic (wrap={false}) block. Without
          // this, the title/caption — plain flowing content with no space guarantee — could
          // land on one page while Yoga pushed the table's actual first chunk to the next,
          // orphaning the title above a blank gap. A single atomic block here is safe: unlike
          // the many-chained-atomic-chunks bug fixed earlier (which came from break-forced
          // chunk N>0 repeating this pattern dozens of times in a row), this is only ever ONE
          // atomic block per width-part — chunks 1+ below stay wrap={true} as before.
          return (
            <View key={`${partKey}-${chunkIdx}`} wrap={false} style={{ alignSelf: 'flex-start', flexGrow: 0 }}>
              {sectionTitle}
              {caption && <PdfTableCaption label={caption.label} title={caption.title} />}
              <View style={styles.table} wrap={false}>
                {headerRows}
                {chunk.map((rowData, rowIdx) => {
                  const rowKey = `${partKey}-${chunkIdx}-${rowIdx}`;
                  const dataRow = (
                    <SoaDataRow
                      rowData={rowData}
                      header={header}
                      colWidths={colWidths}
                      lastCol={lastCol}
                      fonts={fonts}
                      rowIdx={`${scheduleIdx}-${rowKey}`}
                    />
                  );
                  if (!rowData._keyNotesText) {
                    return <React.Fragment key={rowKey}>{dataRow}</React.Fragment>;
                  }
                  return (
                    <View key={rowKey} wrap={false}>
                      {dataRow}
                      <SoaNoteRow
                        text={rowData._keyNotesText}
                        totalWidth={totalTableWidth}
                        fonts={fonts}
                        rowIdx={rowKey}
                      />
                    </View>
                  );
                })}
              </View>
            </View>
          );
        }

        return (
        // Hybrid: wrap on container (no mid-page squash) + flexGrow 0 (no empty stretch).
        // Rows stay wrap={false}. Chunks keep headers attached on each page.
        <View
          key={`${partKey}-${chunkIdx}`}
          break
          wrap
          style={{ alignSelf: 'flex-start', flexGrow: 0 }}
        >
          {/*
            wrap (not false): a SOA schedule commonly renders many consecutive
            chunks, each wrapped in a break={true} sibling. Marking the table
            itself wrap={false} makes it an atomic block — verified via a
            direct @react-pdf/renderer reproduction that once a table's real
            (Yoga-measured) height doesn't leave room before the next chunk's
            forced break, react-pdf does not reliably relocate the whole atomic
            block to a fresh page; instead multiple chunks' content lands on
            the same physical page and renders visually overlapping/garbled.
            Rows already carry their own wrap={false} (below), so leaving the
            table wrappable only lets it flow to a new page between rows —
            it still never splits a row — while restoring correct pagination.
          */}
          <View style={styles.table} wrap>
            {headerRows}
            {chunk.map((rowData, rowIdx) => {
              const rowKey = `${partKey}-${chunkIdx}-${rowIdx}`;
              const dataRow = (
                <SoaDataRow
                  rowData={rowData}
                  header={header}
                  colWidths={colWidths}
                  lastCol={lastCol}
                  fonts={fonts}
                  rowIdx={`${scheduleIdx}-${rowKey}`}
                />
              );
              if (!rowData._keyNotesText) {
                return <React.Fragment key={rowKey}>{dataRow}</React.Fragment>;
              }
              // Pair the activity row with its note row so they relocate to a fresh page
              // together instead of the note ever landing alone, disconnected from the
              // activity it describes.
              return (
                <View key={rowKey} wrap={false}>
                  {dataRow}
                  <SoaNoteRow
                    text={rowData._keyNotesText}
                    totalWidth={totalTableWidth}
                    fonts={fonts}
                    rowIdx={rowKey}
                  />
                </View>
              );
            })}
          </View>
        </View>
        );
      })}
    </View>
  );
}

/** One schedule — may split into multiple width-parts when there are many visits. */
function SingleSoaSchedulePdf({ soaData, fontConfig, caption, scheduleIdx, sectionTitle }) {
  // Key Notes render as a full-width row under each activity (SoaNoteRow) rather than a
  // dedicated column — soaDataToTableRows always attaches the text via `_keyNotesText`
  // regardless of this flag, so the column itself can be omitted here. Dropping it also frees
  // real column width for the visit columns, which need fewer landscape-width splits.
  const { header, rows, footnotesBlock, visitMeta } = soaDataToTableRows(soaData, {
    includeKeyNotesColumn: false,
  });
  if (!header.length || !rows.length) return null;

  const widthParts = splitSoaTableForPdfWidth(header, rows, SOA_PDF_USABLE_WIDTH_PT, visitMeta);

  return (
    <View>
      {widthParts.map((part, partIdx) => (
        <SoaTablePart
          key={`part-${partIdx}`}
          header={part.header}
          rows={part.rows}
          fontConfig={fontConfig}
          caption={partIdx === 0 ? caption : null}
          partKey={`p${partIdx}`}
          scheduleIdx={scheduleIdx}
          visitMeta={part.visitMeta}
          sectionTitle={partIdx === 0 ? sectionTitle : null}
        />
      ))}

      {footnotesBlock.length > 0 && (
        <View wrap>
          <Text
            style={[
              styles.footnotesTitle,
              { fontSize: sanitizePdfPt(fontConfig?.footnote?.size || 9, 9) },
            ]}
          >
            Key Notes
          </Text>
          {footnotesBlock.map((line, fIdx) => {
            const body = stripFootnoteNumberForSoaExport(line);
            if (!body) return null;
            return (
              <Text
                key={fIdx}
                wrap={false}
                style={[
                  styles.footnoteLine,
                  { fontSize: sanitizePdfPt(fontConfig?.footnote?.size || 9, 9), lineHeight: 1.25 },
                ]}
              >
                {renderHybridPdfText(body)}
              </Text>
            );
          })}
        </View>
      )}
    </View>
  );
}

/**
 * Native PDF SOA — readable landscape tables (split wide schedules; no row squash).
 */
export default function SoaPdfTable({ soaData, fontConfig = null, caption = null, sectionTitle = null }) {
  const schedules = listSoaLegacyPayloadsForExport(soaData);
  const payloads =
    schedules.length > 0
      ? schedules
      : soaData?.visits && soaData?.sections
        ? [soaData]
        : [];

  if (!payloads.length) return null;

  return (
    <View>
      {payloads.map((payload, idx) => (
        <SingleSoaSchedulePdf
          key={payload.sourceTableId || `soa-sched-${idx}`}
          soaData={payload}
          fontConfig={fontConfig}
          caption={idx === 0 ? caption : null}
          scheduleIdx={idx}
          sectionTitle={idx === 0 ? sectionTitle : null}
        />
      ))}
    </View>
  );
}
